import React from 'react';
import { motion } from 'framer-motion';
import { AiImageBadge, AiImageFrame } from '../components/AiImageBadge';

const Farm: React.FC = () => {
  // Varianti per le animazioni
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 1 } }
  };

  const slideUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="relative h-[60vh]">
        <div className="absolute inset-0 bg-black bg-opacity-30 z-10"></div>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url("/copertina_serra_senza_villa.png")' }}
        ></div>
        <AiImageBadge position="bottom-right" />

        <div className="container mx-auto px-4 h-full flex items-center relative z-20">
          <div className="max-w-3xl">
            <motion.h1
              className="font-playfair text-4xl md:text-5xl text-white mb-4"
              initial="hidden"
              animate="visible"
              variants={fadeIn}
            >
              La Nostra Azienda Agricola
            </motion.h1>
            <motion.p
              className="font-montserrat text-lg md:text-xl text-white mb-8"
              initial="hidden"
              animate="visible"
              variants={slideUp}
            >
              Coltiviamo con passione prodotti genuini nel rispetto della natura
            </motion.p>
          </div>
        </div>
      </section>

      {/* Storia e Filosofia */}
      <section className="py-20 bg-beige">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-playfair text-3xl text-verde-scuro mb-6">La nostra storia</h2>
              <div className="font-montserrat text-verde-oliva space-y-4 leading-relaxed">
                <p>
                  L'azienda agricola Villa d'Arco nasce dalla passione per la terra e dalla volontà di produrre
                  alimenti genuini e di qualità. Situata nelle colline di Baronissi, la nostra azienda si estende
                  su diversi ettari di terreno fertile, dove coltiviamo con amore e dedizione una varietà di prodotti.
                </p>
                <p>
                  Negli anni abbiamo sviluppato tecniche di coltivazione che rispettano l'ambiente e valorizzano
                  la biodiversità locale. La nostra filosofia è basata sul concetto di agricoltura sostenibile,
                  che mira a preservare le risorse naturali per le generazioni future.
                </p>
                <p>
                  Oggi, oltre a proseguire con impegno la nostra attività agricola, 
                  stiamo lavorando per aprire il ristorante di Villa d’Arco: 
                  un luogo in cui verranno realizzati piatti con le materie prime della nostra azienda e con prodotti locali, così da valorizzare il territorio campano,
                   attingendo ad esempio anche alle eccellenze del Cilento.
                </p>
              </div>
            </motion.div>
            <motion.div
              className="rounded-lg overflow-hidden shadow-xl"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <img src="/Azienda-agricola.jpg" alt="Villa d'Arco" className="w-full h-auto" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Le nostre coltivazioni */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.h2
            className="font-playfair text-3xl text-verde-scuro mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Le nostre coltivazioni principali
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Funghi Cardoncelli */}
            <motion.div
              className="bg-beige rounded-lg overflow-hidden shadow-md"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="h-64 overflow-hidden">
                <AiImageFrame className="w-full h-full" compact>
                  <img
                    src="/prodotto-1.jpg"
                    alt="Funghi Cardoncelli (immagine generata con intelligenza artificiale)"
                    className="w-full h-full object-cover"
                  />
                </AiImageFrame>
              </div>
              <div className="p-6">
                <h3 className="font-playfair text-2xl text-verde-scuro mb-4">Funghi Cardoncelli</h3>
                <p className="font-montserrat text-verde-oliva mb-4">
                  La nostra serra fungaia è dedicata alla coltivazione dei pregiati funghi Cardoncelli,
                  noti per il loro sapore delicato e la consistenza carnosa. Utilizziamo tecniche tradizionali
                  per garantire la massima qualità e genuinità.
                </p>
                <p className="font-montserrat text-verde-oliva">
                  I nostri funghi sono disponibili freschi tutto l'anno e sono l'ingrediente principale
                  di molti piatti del nostro ristorante.
                </p>
              </div>
            </motion.div>

            {/* Ortaggi */}
            <motion.div
              className="bg-beige rounded-lg overflow-hidden shadow-md"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="h-64 overflow-hidden">
                <AiImageFrame className="w-full h-full" compact>
                  <img
                    src="/prodotto-2.jpg"
                    alt="Ortaggi (immagine generata con intelligenza artificiale)"
                    className="w-full h-full object-cover"
                  />
                </AiImageFrame>
              </div>
              <div className="p-6">
                <h3 className="font-playfair text-2xl text-verde-scuro mb-4">Ortaggi di stagione</h3>
                <p className="font-montserrat text-verde-oliva mb-4">
                  Nei nostri campi coltiviamo una vasta gamma di ortaggi di stagione, seguendo il ritmo
                  naturale delle stagioni. Pomodori, zucchine, peperoni, melanzane e molti altri prodotti
                  crescono nei nostri terreni senza l'uso di pesticidi chimici.
                </p>
                <p className="font-montserrat text-verde-oliva">
                  La freschezza e il sapore autentico dei nostri ortaggi sono il risultato di pratiche
                  agricole rispettose dell'ambiente e di una cura costante.
                </p>
              </div>
            </motion.div>

            {/* Olio di Aquara */}
            <motion.div
              className="bg-beige rounded-lg overflow-hidden shadow-md"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="h-64 overflow-hidden">
                <AiImageFrame className="w-full h-full" compact>
                  <img
                    src="/olio_nostra-produzione.png"
                    alt="Olio di Aquara (immagine generata con intelligenza artificiale)"
                    className="w-full h-full object-cover"
                  />
                </AiImageFrame>
              </div>
              <div className="p-6">
                <h3 className="font-playfair text-2xl text-verde-scuro mb-4">Olio di Aquara</h3>
                <p className="font-montserrat text-verde-oliva mb-4">
                  Il nostro olio extravergine di oliva nasce dalle colline di Aquara,
                  dove il clima e il terreno donano alle olive un aroma intenso e armonioso.
                  Raccogliamo e frangiamo le olive con metodi tradizionali, per preservare al meglio profumo,
                  gusto e proprietà nutrizionali.
                </p>
                <p className="font-montserrat text-verde-oliva">
                  Dal colore dorato con riflessi verdi e dal sapore equilibrato,
                  il nostro olio è ideale per esaltare ogni piatto,
                  dalla cucina quotidiana alle ricette più raffinate del nostro ristorante.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Processo di coltivazione 
      <section className="py-20 bg-crema">
        <div className="container mx-auto px-4">
          <motion.h2
            className="font-playfair text-3xl text-verde-scuro mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Il nostro processo di coltivazione
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              className="order-2 md:order-1"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="space-y-8">
                <div>
                  <h3 className="font-playfair text-2xl text-verde-scuro mb-3">1. Preparazione del terreno</h3>
                  <p className="font-montserrat text-verde-oliva">
                    Prepariamo il terreno con tecniche naturali, utilizzando compost organico e
                    rotazione delle colture per mantenere la fertilità del suolo senza l'uso di
                    fertilizzanti chimici.
                  </p>
                </div>
                <div>
                  <h3 className="font-playfair text-2xl text-verde-scuro mb-3">2. Semina e piantagione</h3>
                  <p className="font-montserrat text-verde-oliva">
                    Utilizziamo sementi selezionate e tecniche di piantagione che rispettano i
                    cicli naturali delle piante, favorendo la biodiversità e la resilienza delle colture.
                  </p>
                </div>
                <div>
                  <h3 className="font-playfair text-2xl text-verde-scuro mb-3">3. Cura e manutenzione</h3>
                  <p className="font-montserrat text-verde-oliva">
                    Ogni giorno ci prendiamo cura delle nostre coltivazioni con metodi naturali di
                    controllo dei parassiti e irrigazione ottimizzata per ridurre il consumo di acqua.
                  </p>
                </div>
                <div>
                  <h3 className="font-playfair text-2xl text-verde-scuro mb-3">4. Raccolta</h3>
                  <p className="font-montserrat text-verde-oliva">
                    I nostri prodotti vengono raccolti a mano al momento giusto di maturazione,
                    garantendo la massima freschezza e qualità organolettica.
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div
              className="rounded-lg overflow-hidden shadow-xl order-1 md:order-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <img src="/processazione_terreno.jpg" alt="Processo di coltivazione" className="w-full h-auto" />
            </motion.div>
          </div>
        </div>
      </section>*/}

      {/* Sostenibilità */}
      <section className="py-20 bg-verde-oliva text-white">
        <div className="container mx-auto px-4">
          <motion.h2
            className="font-playfair text-3xl mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Il nostro impegno per la sostenibilità
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="bg-white bg-opacity-10 p-8 rounded-lg h-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-6 text-oro" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <h3 className="font-playfair text-2xl mb-4">Coltivazione naturale</h3>
                <p className="font-montserrat">
                  Coltiviamo i nostri prodotti con metodi ispirati ai principi dell’agricoltura biologica,
                  evitando l’uso di pesticidi e fertilizzanti chimici,
                  per proteggere la salute del suolo, dell’acqua e della biodiversità.
                </p>
              </div>
            </motion.div>

            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="bg-white bg-opacity-10 p-8 rounded-lg h-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-6 text-oro" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="font-playfair text-2xl mb-4">Risparmio idrico</h3>
                <p className="font-montserrat">
                  Per noi l’acqua è una risorsa preziosa. Utilizziamo sistemi di irrigazione a goccia e
                  timer programmati per garantire alle piante solo l’acqua di cui hanno bisogno,
                  riducendo gli sprechi e rispettando l’ambiente.
                </p>
              </div>
            </motion.div>

            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="bg-white bg-opacity-10 p-8 rounded-lg h-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-6 text-oro" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                </svg>
                <h3 className="font-playfair text-2xl mb-4">Filiera corta</h3>
                <p className="font-montserrat">
                  Promuoviamo la filiera corta vendendo direttamente i nostri prodotti e
                  utilizzandoli nel nostro ristorante, riducendo l'impatto ambientale
                  legato al trasporto e garantendo la massima freschezza.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action 
      <section className="py-16 bg-beige">
        <div className="container mx-auto px-4 text-center">
          <motion.h2
            className="font-playfair text-3xl text-verde-scuro mb-6"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Scopri i nostri prodotti
          </motion.h2>
          <motion.p
            className="font-montserrat text-verde-oliva mb-8 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Visita il nostro negozio online per acquistare i prodotti freschi della nostra azienda agricola
            o vieni a trovarci per un'esperienza diretta nei nostri campi.
          </motion.p>
          <motion.a
            href="/prodotti"
            className="btn-primary"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            Acquista ora
          </motion.a>
        </div>
      </section>*/}
      <div className=" bg-beige flex flex-col items-center justify-center px-4 py-16"></div> {/*Aggiunto dopo */}

    </div>
  );
};

export default Farm;
